import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  MenuItem, Divider, Chip
} from '@mui/material';
import { ArrowBack, AccountBalance, Warning, AttachFile } from '@mui/icons-material';
import axios from 'axios';
import { uploadAttachment } from './AttachmentsPanel';
import { fmt, sym } from '../utils/currency';
import { useSuppliers } from '../hooks/useSuppliers';
import { useBankAccounts } from '../hooks/useBankAccounts';

const PaymentVoucherForm = () => {
  const navigate = useNavigate();
  const { state: navState } = useLocation();

  const { suppliers } = useSuppliers();
  const { banks } = useBankAccounts();

  const [supplierCode, setSupplierCode] = useState(navState?.supplierCode || '');
  const [invoices, setInvoices] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState(navState?.invoiceNo || '');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [bankAccountNo, setBankAccountNo] = useState('');
  const [amount, setAmount] = useState('');
  const [whtRate, setWhtRate] = useState('');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState(null); // supporting document (invoice scan, approval…)
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Load invoices when supplier changes
  useEffect(() => {
    if (!supplierCode) { setInvoices([]); setInvoiceNo(''); setSelectedInvoice(null); return; }
    axios.get(`${process.env.REACT_APP_API_URL}/api/payables/invoices`, { headers })
      .then(r => {
        const open = r.data.filter(i => i.supplierCode === supplierCode && ['Open', 'Partial'].includes(i.status));
        setInvoices(open);
        // If pre-selected from navigation, keep it
        if (navState?.invoiceNo) {
          const inv = open.find(i => i.invoiceNo === navState.invoiceNo);
          if (inv) { setSelectedInvoice(inv); setAmount((inv.totalAmount - inv.paid).toFixed(2)); }
        }
      })
      .catch(() => setInvoices([]));
  }, [supplierCode]); // eslint-disable-line

  const handleInvoiceChange = (no) => {
    setInvoiceNo(no);
    const inv = invoices.find(i => i.invoiceNo === no);
    setSelectedInvoice(inv || null);
    if (inv) setAmount((inv.totalAmount - inv.paid).toFixed(2));
    else setAmount('');
  };

  const handleSubmit = async () => {
    if (!supplierCode) { setError('Supplier is required'); return; }
    if (!invoiceNo) { setError('Invoice is required'); return; }
    if (!bankAccountNo) { setError('Bank account is required'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Amount must be greater than zero'); return; }
    if (!reference.trim()) { setError('Reference is required'); return; }
    if (insufficientFunds) {
      setError(`Insufficient bank balance — available ${sym()} ${fmt(bankBalance)}, payment ${sym()} ${fmt(amountNum)}`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payables/vouchers`,
        { invoiceNo, bankAccountNo, amount: parseFloat(amount), whtRate: parseFloat(whtRate) || 0, reference, description, voucherDate },
        { headers }
      );
      // Voucher is posted — hang the supporting document on it
      let message = data.message;
      if (file && data.voucherNo) {
        try { await uploadAttachment(file, 'PaymentVoucher', data.voucherNo); }
        catch { message = `${data.message} — but the attachment failed to upload`; }
      }
      navigate('/payables/vouchers', { state: { message } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post payment voucher');
    } finally {
      setSaving(false);
    }
  };

  const outstanding     = selectedInvoice ? (selectedInvoice.totalAmount - selectedInvoice.paid) : 0;
  const selectedBankObj = banks.find(b => b.accountNo === bankAccountNo) || null;
  const bankBalance     = selectedBankObj?.balance ?? null;
  const amountNum       = parseFloat(amount) || 0;
  const whtNum          = Math.round(amountNum * (parseFloat(whtRate) || 0)) / 100;
  const netNum          = Math.round((amountNum - whtNum) * 100) / 100;
  const insufficientFunds = bankBalance !== null && netNum > 0 && netNum > bankBalance;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/payables/vouchers')} sx={{ mr: 2 }}>
              Back
            </Button>
            <Typography variant="h5" color="primary">New Payment Voucher</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField select label="Supplier" value={supplierCode}
              onChange={e => setSupplierCode(e.target.value)} fullWidth size="small">
              <MenuItem value=""><em>Select Supplier</em></MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.supplierCode} value={s.supplierCode}>
                  {s.supplierCode} – {s.supplierName}
                </MenuItem>
              ))}
            </TextField>

            <TextField select label="Purchase Invoice" value={invoiceNo}
              onChange={e => handleInvoiceChange(e.target.value)}
              fullWidth size="small" disabled={!supplierCode}>
              <MenuItem value=""><em>{supplierCode ? 'Select Invoice' : 'Select supplier first'}</em></MenuItem>
              {invoices.map(i => (
                <MenuItem key={i.invoiceNo} value={i.invoiceNo}>
                  {i.invoiceNo} — {sym()} {fmt(i.totalAmount - i.paid)} outstanding
                </MenuItem>
              ))}
            </TextField>

            {selectedInvoice && (
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  Invoice Total: <strong>{sym()} {fmt(selectedInvoice.totalAmount)}</strong>
                  {selectedInvoice.totalVAT > 0 && <> (incl. VAT {sym()} {fmt(selectedInvoice.totalVAT)})</>}
                  &nbsp;|&nbsp; Paid: <strong>{sym()} {fmt(selectedInvoice.paid)}</strong>
                  &nbsp;|&nbsp; Outstanding: <strong>{sym()} {fmt(outstanding)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  AP GL: {selectedInvoice.apGLAccount}
                </Typography>
              </Box>
            )}

            <Divider />

            <TextField select label="Paying Bank Account" value={bankAccountNo}
              onChange={e => setBankAccountNo(e.target.value)} fullWidth size="small">
              <MenuItem value=""><em>Select Bank</em></MenuItem>
              {banks.map(b => (
                <MenuItem key={b.accountNo} value={b.accountNo}>
                  {b.bankName} – {b.accountName} ({b.accountNo}) | GL: {b.glAccount}
                </MenuItem>
              ))}
            </TextField>

            {selectedBankObj && (
              <Box sx={{
                p: 1.5, borderRadius: 1,
                bgcolor: insufficientFunds ? 'error.50' : 'success.50',
                border: '1px solid',
                borderColor: insufficientFunds ? 'error.200' : 'success.200',
                display: 'flex', alignItems: 'center', gap: 1.5
              }}>
                <AccountBalance sx={{ color: insufficientFunds ? 'error.main' : 'success.main', fontSize: 20 }} />
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    {selectedBankObj.bankName} — {selectedBankObj.accountName} available balance
                  </Typography>
                  <Typography variant="body2" fontWeight={700}
                    color={insufficientFunds ? 'error.main' : 'success.main'}>
                    {sym()} {fmt(bankBalance)}
                  </Typography>
                </Box>
                {insufficientFunds && (
                  <Chip
                    icon={<Warning />}
                    label={`Shortfall: ${sym()} ${fmt(amountNum - bankBalance)}`}
                    color="error" size="small" variant="outlined"
                  />
                )}
                {!insufficientFunds && amountNum > 0 && (
                  <Chip
                    label={`After payment: ${sym()} ${fmt(bankBalance - amountNum)}`}
                    color="success" size="small" variant="outlined"
                  />
                )}
              </Box>
            )}

            <TextField label="Voucher Date" type="date" value={voucherDate}
              onChange={e => setVoucherDate(e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />

            <TextField label={`Amount (${sym()})`} value={amount}
              onChange={e => setAmount(e.target.value)}
              type="number" inputProps={{ min: 0, max: outstanding, step: '0.01' }}
              fullWidth size="small"
              helperText={outstanding > 0 ? `Max: ${sym()} ${fmt(outstanding)}` : ''} />

            <TextField label="Withholding Tax (%)" value={whtRate}
              onChange={e => setWhtRate(e.target.value)}
              type="number" inputProps={{ min: 0, max: 100, step: '0.1' }}
              fullWidth size="small"
              helperText={whtNum > 0
                ? `WHT withheld: ${sym()} ${fmt(whtNum)} · Net paid from bank: ${sym()} ${fmt(netNum)}`
                : 'Optional — leave blank if no WHT applies'} />

            <TextField label="Reference (e.g. Cheque No / EFT Ref)" value={reference}
              onChange={e => setReference(e.target.value.toUpperCase())}
              required fullWidth size="small"
              inputProps={{ style: { textTransform: 'uppercase' } }} />

            <TextField label="Description" value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth size="small" multiline rows={2} />

            <Box display="flex" alignItems="center" gap={1}>
              <Button component="label" size="small" startIcon={<AttachFile />} sx={{ textTransform: 'none' }}>
                Attach supporting document
                <input type="file" hidden onChange={e => setFile(e.target.files?.[0] || null)} />
              </Button>
              {file && <Chip label={file.name} size="small" onDelete={() => setFile(null)} />}
            </Box>
          </Box>

          <Box display="flex" gap={2} mt={3}>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Posting…' : 'Post Payment Voucher'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/payables/vouchers')}>Cancel</Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentVoucherForm;
